import React, { FunctionComponent, useEffect } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import poolsNetworkImg from '@assets/pools_network.png';
import { RouteComponentProps } from '@reach/router';
import { ChainId, useEthers } from '@usedapp/core';

import Button from '@bitcoin-portal/verse-web-components/dist/Button';
import H1 from '@bitcoin-portal/verse-web-components/dist/H1';
import Title from '@bitcoin-portal/verse-web-components/dist/Title';
import { colors } from '@bitcoin-portal/verse-web-components/dist/themes/colors';

import { DEFAULT_CHAIN } from '@context/constants';
import { ChainValidationHandler } from '@context/handlers';
import { useTrackedState } from '@context/store';

import { findProvider, isDeFiProvider } from '@helpers/index';

import { Header, Message, NoViewWrapper } from './styled';

interface Props extends RouteComponentProps {
  title: string;
  subtitle: string;
  switchProviderMsg: string;
  switchNetworkMsg: string;
  invalidProvider: boolean;
  isInvalidNetwork?: boolean;
  minWidth?: string;
  useBg?: boolean;
  routesEnabled?: boolean;
  page?: string;
}

export const NoView: FunctionComponent<Props> = ({
  title,
  subtitle,
  switchProviderMsg,
  switchNetworkMsg,
  invalidProvider,
  isInvalidNetwork,
  minWidth,
  useBg,
  routesEnabled,
  page,
}) => {
  const { account, chainId, switchNetwork } = useEthers();
  const { isWallet, provider: exchangeProvider } = useTrackedState();

  const { formatMessage } = useIntl();

  const provider = findProvider(exchangeProvider);
  const correctChain = provider.supportedChains?.[0]?.chainId || DEFAULT_CHAIN;

  const invalidNetwork =
    isInvalidNetwork ||
    (isDeFiProvider(exchangeProvider) &&
      account &&
      (chainId !== correctChain || !chainId));

  useEffect(() => {
    if (isWallet && invalidNetwork) {
      switchNetwork(correctChain);
    }
  }, []);

  return (
    <>
      {routesEnabled && <ChainValidationHandler page={page} />}
      <NoViewWrapper minWidth={minWidth} bg={useBg}>
        <Header>
          <H1>{title}</H1>
          <Title style={{ color: colors.shade80 }}>{subtitle}</Title>
        </Header>

        <Message>
          {invalidProvider && (
            <div style={{ maxWidth: 411 }}>
              <img
                src={poolsNetworkImg}
                alt="switch to defi graphic"
                width="184px"
              />
              <Title
                style={{
                  color: colors.shade90,
                  paddingTop: 24,
                }}
              >
                {switchProviderMsg}
              </Title>
            </div>
          )}
          {!invalidProvider && invalidNetwork && (
            <div style={{ maxWidth: 411 }}>
              <img
                src={poolsNetworkImg}
                alt="switch networks graphic"
                width="184px"
              />
              <Title
                style={{
                  color: colors.shade90,
                  paddingTop: 24,
                }}
              >
                {isWallet
                  ? formatMessage(
                      { id: 'staking.noView.switchNetworkWallet' },
                      { PROVIDER: provider.label },
                    )
                  : switchNetworkMsg}
              </Title>
              {invalidNetwork && !isWallet && (
                <Button
                  fullWidth
                  design="flat"
                  style={{
                    marginTop: 24,
                  }}
                  onClick={async () => {
                    try {
                      if (exchangeProvider === 'bitcoincom_ply') {
                        await switchNetwork(ChainId.Polygon);
                      } else {
                        await switchNetwork(ChainId.Mainnet);
                      }
                    } catch (e) {
                      console.error(e);
                    }
                  }}
                >
                  <FormattedMessage id="global.labels.switchNetwork" />
                </Button>
              )}
            </div>
          )}
          {!invalidProvider && !invalidNetwork && (
            <Title style={{ color: colors.shade90 }}>
              Hmm, something went wrong. Please try again later.
            </Title>
          )}
        </Message>
      </NoViewWrapper>
    </>
  );
};

export default NoView;
